'use client';
import Styles from './order-summary.module.scss';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import {
  formatCurrency,
  getPriceTotal,
  getDeliveryLines,
  getProductLines,
  getNonProductLines,
} from '~/utils/utils';
import {
  useSelectedPaymentOption,
  usePaymentReview,
  useProgressiveDetails,
} from '~/store/wmap-store';
import { TYPES } from '~/modules/payment/constants';
import ProgressiveSignLease from '~/modules/payment/progressive/components/progressive-sign-lease';
import { IGetPricing } from '~/interfaces/commerce-types';

const OrderSummary: React.FC<{
  salesOrderResponse: ISalesOrderResponse;
  userSelectedAmount?: number | undefined;
  isConfirmation?: boolean | false;
  parsedProgressiveDetails?: IGetPricing | undefined;
  selectedPaymentOption?: string | undefined;
  isProgressive?: boolean | false;
}> = ({
  salesOrderResponse,
  userSelectedAmount,
  isConfirmation,
  parsedProgressiveDetails,
  isProgressive,
}) => {
  const paymentReview = usePaymentReview();
  const progressiveData = useProgressiveDetails();
  const progressiveDetails = parsedProgressiveDetails || progressiveData;
  const selectedPaymentOption = useSelectedPaymentOption();
  if (!salesOrderResponse) {
    return <div>Failed to load order summary.</div>;
  }
  const {
    TotalRecyclingFee = 0,
    TotalTax = 0,
    TotalDiscount = 0,
    TotalAmount = 0,
    AmountPaid = 0,
    AmountDue = 0,
    ProductList = [],
    ZipCode = 0,
  } = salesOrderResponse || {};
  const isProgressiveReview: boolean =
    ((selectedPaymentOption === TYPES.PROGRESIVE || !!isProgressive) &&
      (paymentReview.review || isConfirmation)) ||
    false;

  const productsArray = getProductLines(ProductList);
  const deliveryArray = getDeliveryLines(ProductList);
  const deliveryPrice = getPriceTotal(deliveryArray);
  const donationsArray = getNonProductLines(ProductList);
  const itemsPrice =
    getPriceTotal(productsArray) + getPriceTotal(donationsArray) || 0;
  const amountPaidText = () => {
    const totalAmount = AmountPaid + (userSelectedAmount || 0);

    if (userSelectedAmount) {
      return totalAmount > 0
        ? `-${formatCurrency(totalAmount)}`
        : formatCurrency(totalAmount);
    }

    return AmountPaid > 0
      ? `-${formatCurrency(AmountPaid)}`
      : formatCurrency(AmountPaid);
  };
  const discountAdjustment = TotalDiscount < 0 ? Math.abs(TotalDiscount) : 0;
  const totalAmount = TotalAmount;
  const amountDue = AmountDue;

  return (
    <div
      className={`${Styles.orderSummaryWrapper} ${isConfirmation && Styles.confirmation}`}
    >
      <div className={Styles.orderSummaryDetails}>
        <div className={Styles.orderSummaryLines} tabIndex={0}>
          <span className={Styles.label}>Subtotal</span>
          <span className={Styles.value}>{formatCurrency(itemsPrice)}</span>
        </div>
        {discountAdjustment > 0 && (
          <div className={Styles.orderSummaryLines} tabIndex={0}>
            <span className={Styles.label}>Adjustment</span>
            <span className={Styles.value}>
              {formatCurrency(discountAdjustment)}
            </span>
          </div>
        )}
        {TotalDiscount > 0 && (
          <div
            className={`${Styles.orderSummaryLines} ${Styles.discount}`}
            tabIndex={0}
          >
            <span className={Styles.label}>SAVINGS</span>
            <span className={Styles.value}>
              -{formatCurrency(TotalDiscount)}
            </span>
          </div>
        )}
        {deliveryPrice > 0 && (
          <div className={Styles.orderSummaryLines} tabIndex={0}>
            <span className={Styles.label}>Delivery</span>
            <span className={Styles.value}>
              {formatCurrency(deliveryPrice)}
            </span>
          </div>
        )}
        {!isProgressiveReview && (
          <div className={Styles.orderSummaryLines} tabIndex={0}>
            <span className={Styles.label}>Tax - {ZipCode}</span>
            <span className={Styles.value}>{formatCurrency(TotalTax)}</span>
          </div>
        )}
        {!isProgressiveReview && TotalRecyclingFee > 0 && (
          <div className={Styles.orderSummaryLines} tabIndex={0}>
            <span className={Styles.label}>Recycling Fee</span>
            <span className={Styles.value}>
              {formatCurrency(TotalRecyclingFee)}
            </span>
          </div>
        )}
        {isProgressiveReview ? (
          <ProgressiveSummary
            salesOrderResponse={salesOrderResponse}
            progressiveDetails={progressiveDetails}
            isConfirmation={isConfirmation}
          />
        ) : (
          <>
            <div className={Styles.divider}></div>
            <div
              className={`${Styles.orderSummaryLines} ${Styles.total}`}
              tabIndex={0}
            >
              <span className={Styles.label}>Total</span>
              <span className={Styles.value}>
                <span className={Styles.currencyLabel}>USD</span>
                {formatCurrency(totalAmount)}
              </span>
            </div>
            <div className={Styles.orderSummaryLines} tabIndex={0}>
              <span className={Styles.label}>Amount Paid</span>
              <span className={Styles.value}>{amountPaidText()}</span>
            </div>
            <div
              className={`${Styles.orderSummaryLines} ${Styles.greenLine}`}
              tabIndex={0}
            >
              <span className={Styles.label}>Amount Due</span>
              <span className={Styles.value}>
                {userSelectedAmount
                  ? formatCurrency(amountDue - Number(userSelectedAmount))
                  : formatCurrency(amountDue)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderSummary;

const ProgressiveSummary: React.FC<{
  salesOrderResponse: ISalesOrderResponse;
  progressiveDetails?: IGetPricing | undefined;
  isConfirmation?: boolean | false;
}> = ({ salesOrderResponse, progressiveDetails, isConfirmation }) => {
  const retailerFees =
    progressiveDetails?.PaymentDueAtSigning?.TotalRetailerFeeAmount || 0;
  return (
    <>
      <div className={Styles.orderSummaryLines} tabIndex={0}>
        <span className={Styles.label}>Lease-to-Own Cost</span>
        <span className={Styles.value}>
          {formatCurrency(progressiveDetails?.Term?.LeaseCostToOwn || 0)}
        </span>
      </div>
      <div className={Styles.divider}></div>
      <div className={Styles.progressiveSummary}>
        <div className={Styles.orderSummaryLines} tabIndex={0}>
          <span className={Styles.label}>
            <b>12-Month Lease-to-Own Total</b>
          </span>
          <span className={Styles.value}>
            <b>{formatCurrency(progressiveDetails?.Term?.SubTotal || 0)}</b>
          </span>
        </div>
        <p className={Styles.progressiveDisclaimer} tabIndex={0}>
          *Applicable taxes will be added to all payments and collected by
          Progressive Leasing.
        </p>
        <div className={Styles.progressiveTotal}>
          <h4 tabIndex={0}>Payment Due at Signing</h4>
          <div className={Styles.orderSummaryLines} tabIndex={0}>
            <span className={Styles.label}>Initial Payment</span>
            <span className={Styles.value}>
              {formatCurrency(
                progressiveDetails?.PaymentDueAtSigning?.InitialPayment || 0,
              )}
            </span>
          </div>
          <div className={Styles.orderSummaryLines} tabIndex={0}>
            <span className={Styles.label}>Total Tax</span>
            <span className={Styles.value}>
              {formatCurrency(
                progressiveDetails?.PaymentDueAtSigning?.TotalTax || 0,
              )}
            </span>
          </div>
          {retailerFees > 0 && (
            <>
              {progressiveDetails?.PaymentDueAtSigning?.RetailerFees?.map(
                (fee, idx) => (
                  <div
                    className={Styles.orderSummaryLines}
                    tabIndex={0}
                    key={idx}
                  >
                    <span className={`${Styles.label} text-capitalize`}>
                      {(
                        fee?.MerchantTransactionTypeName || 'Fee'
                      ).toLowerCase()}
                    </span>
                    <span className={Styles.value}>
                      {formatCurrency(fee?.TransactionAmount || 0)}
                    </span>
                  </div>
                ),
              )}
            </>
          )}
          <div className={Styles.divider} />

          <div className={Styles.orderSummaryLines} tabIndex={0}>
            <span className={Styles.label}>Total</span>
            <span className={Styles.value}>
              {formatCurrency(
                progressiveDetails?.PaymentDueAtSigning?.TotalDueAtSigning || 0,
              )}
            </span>
          </div>
        </div>
        {!isConfirmation && (
          <ProgressiveSignLease orderDetails={salesOrderResponse} />
        )}
      </div>
    </>
  );
};
